import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { AlertTriangle, ArrowLeft } from 'lucide-react'
import { apiGet, apiPost } from '../../lib/api'
import { VoidInvoiceInput } from '@buddys-choice/shared'
import type { Invoice, VoidInvoiceInput as VoidInvoiceInputType } from '@buddys-choice/shared'

export const AdminInvoiceVoidPage: React.FC = () => {
  const { invoiceId } = useParams({ from: '/admin/invoices/$invoiceId/void' })
  const navigate = useNavigate()
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [loadError, setLoadError] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitError, setSubmitError] = useState('')

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors, isSubmitting },
  } = useForm<VoidInvoiceInputType>({
    resolver: zodResolver(VoidInvoiceInput),
    defaultValues: { reason: '' },
  })

  useEffect(() => {
    apiGet<Invoice[]>('invoices')
      .then((rows) => {
        const found = rows.find((i) => i.id === invoiceId)
        if (!found) setLoadError('Invoice not found.')
        else setInvoice(found)
      })
      .catch((err: Error) => setLoadError(err.message || 'Failed to load invoice.'))
      .finally(() => setLoading(false))
  }, [invoiceId])

  const handleSubmit = async (data: VoidInvoiceInputType) => {
    setSubmitError('')
    try {
      await apiPost(`invoices/${invoiceId}/void`, { reason: data.reason.trim() })
      navigate({ to: '/admin/invoices' })
    } catch (err: unknown) {
      setSubmitError((err as Error).message || 'Failed to void invoice.')
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <span className="spinner text-[var(--gold-dark)]" />
      </div>
    )
  }

  if (loadError || !invoice) {
    return (
      <div className="space-y-4">
        <Link to="/admin/invoices" className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)]">
          <ArrowLeft className="w-4 h-4" /> Back to invoices
        </Link>
        <div className="alert alert-error text-sm">{loadError || 'Invoice not found.'}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-lg">
      <Link to="/admin/invoices" className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)]">
        <ArrowLeft className="w-4 h-4" /> Back to invoices
      </Link>

      <div className="glass-card p-6 border border-red-500/20">
        <div className="flex items-center gap-2 text-red-400 mb-2">
          <AlertTriangle className="w-5 h-5" />
          <h1 className="text-xl font-serif font-bold text-[var(--text-primary)]">Void GST invoice</h1>
        </div>
        <p className="text-xs text-[var(--text-muted)] mb-2">{invoice.invoiceNumber}</p>
        <p className="text-xs text-[var(--text-muted)] mb-6">
          Invoices are never hard-deleted. Voiding marks status as void and requires a logged reason.
        </p>

        {submitError && <div className="alert alert-error text-xs mb-4">{submitError}</div>}

        <form onSubmit={handleFormSubmit(handleSubmit)} className="space-y-4 text-sm">
          <div className="form-group">
            <label className="form-label">Mandatory audit reason (min 10 chars) *</label>
            <textarea
              {...register('reason')}
              rows={4}
              placeholder="e.g. Customer cancelled order before dispatch."
              className="form-input"
              aria-invalid={!!errors.reason}
            />
            {errors.reason && <span className="form-error">{errors.reason.message}</span>}
          </div>

          <div className="flex gap-3 pt-2">
            <Link to="/admin/invoices" className="btn btn-secondary flex-1 text-center">
              Cancel
            </Link>
            <button type="submit" disabled={isSubmitting} className="btn btn-danger flex-1 font-semibold">
              {isSubmitting ? <span className="spinner" /> : 'Confirm void'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
